import { create } from 'zustand';

// Registre des brouillons en cours.
//
// Un écran qui garde une saisie non enregistrée (fiche employé, barre
// d'ajout des Paramètres, carte d'arbitrage…) se déclare ici tant qu'il
// est « sale ». Trois sorties consultent ce registre avant d'agir : le
// changement de société (EntitySelect), le changement de page (blocker
// du routeur dans AppShell) et la fermeture de l'onglet (beforeunload).
//
// Les sauvegardes automatiques différées (notes de la Saisie, note de
// paie, grille d'évaluation) ne sont pas des brouillons : elles
// s'enregistrent d'elles-mêmes. Elles posent un « flusher » qui vide
// immédiatement le délai en attente au moment de partir.

export interface UnsavedEntry {
  id: string;
  /** Écran, tel qu'affiché dans la popup (« Fiche de Karim B. »). */
  label: string;
  /** Résumé court (« 3 modifications »). */
  summary?: string;
  /** Enregistre puis résout ; rejette si l'enregistrement échoue.
   *  Absent = brouillon non enregistrable en l'état (Abandonner / Rester). */
  save?: () => Promise<void>;
  /** Jette le brouillon (remet l'écran à l'état propre). */
  discard?: () => void;
}

export interface UnsavedRequest {
  /** Où l'on va (« Octera », « Grille mensuelle »). */
  target: string;
  resolve: (proceed: boolean) => void;
}

interface UnsavedState {
  entries: Record<string, UnsavedEntry>;
  flushers: Record<string, () => void>;
  request: UnsavedRequest | null;
  setEntry: (entry: UnsavedEntry) => void;
  clearEntry: (id: string) => void;
  setFlusher: (id: string, fn: () => void) => void;
  clearFlusher: (id: string) => void;
  setRequest: (request: UnsavedRequest | null) => void;
}

export const useUnsavedStore = create<UnsavedState>((set) => ({
  entries: {},
  flushers: {},
  request: null,
  setEntry: (entry) => set((s) => ({ entries: { ...s.entries, [entry.id]: entry } })),
  clearEntry: (id) =>
    set((s) => {
      if (!(id in s.entries)) return s;
      const entries = { ...s.entries };
      delete entries[id];
      return { entries };
    }),
  setFlusher: (id, fn) => set((s) => ({ flushers: { ...s.flushers, [id]: fn } })),
  clearFlusher: (id) =>
    set((s) => {
      if (!(id in s.flushers)) return s;
      const flushers = { ...s.flushers };
      delete flushers[id];
      return { flushers };
    }),
  setRequest: (request) => set({ request }),
}));

export function listUnsaved(): UnsavedEntry[] {
  return Object.values(useUnsavedStore.getState().entries);
}

export function hasUnsaved(): boolean {
  return Object.keys(useUnsavedStore.getState().entries).length > 0;
}

/** Vide toutes les sauvegardes automatiques en attente. */
export function flushPendingSaves(): void {
  for (const fn of Object.values(useUnsavedStore.getState().flushers)) fn();
}

/**
 * Demande la permission de quitter. Résout `true` quand on peut partir
 * (rien à sauver, ou l'utilisateur a enregistré / abandonné), `false`
 * quand il préfère rester. La popup est rendue par UnsavedChangesHost.
 */
export function confirmUnsaved(target: string): Promise<boolean> {
  flushPendingSaves();
  if (!hasUnsaved()) return Promise.resolve(true);
  const st = useUnsavedStore.getState();
  // Une popup déjà ouverte : la deuxième demande est refusée plutôt que
  // d'empiler deux décisions contradictoires.
  if (st.request) return Promise.resolve(false);
  return new Promise((resolve) => st.setRequest({ target, resolve }));
}

/** Tests / garde-fous : remet le registre à zéro. */
export function resetUnsaved(): void {
  const st = useUnsavedStore.getState();
  st.request?.resolve(false);
  useUnsavedStore.setState({ entries: {}, flushers: {}, request: null });
}
